import { CheckCircleIcon, XIcon } from '@phosphor-icons/react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { ManageGroupsContent } from '@/content/ManageGroupsContent';

export interface ArchiveGroupDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'archive' | 'restore';
  groupName: string;
  isSubmitting: boolean;
  onConfirm: () => Promise<void>;
}

export function ArchiveGroupDrawer({
  open,
  onOpenChange,
  mode,
  groupName,
  isSubmitting,
  onConfirm,
}: ArchiveGroupDrawerProps) {
  const content = ManageGroupsContent.archiveDrawer;
  const isArchive = mode === 'archive';

  const title = isArchive
    ? content.archiveTitle(groupName)
    : content.restoreTitle(groupName);
  const description = isArchive
    ? content.archiveDescription
    : content.restoreDescription;
  const bullets = isArchive ? content.archiveBullets : content.restoreBullets;
  const confirmLabel = isArchive
    ? content.archiveConfirm
    : content.restoreConfirm;
  const loadingLabel = isArchive ? content.archiving : content.restoring;

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        aria-label={title}
        className="max-h-[92vh] rounded-t-[20px] bg-slate-700 px-4 pb-6 pt-4 sm:left-1/2 sm:max-w-lg sm:-translate-x-1/2 sm:px-5"
      >
        <div className="mx-auto flex max-h-[calc(92vh-3rem)] w-full max-w-lg flex-col">
          <DrawerHeader className="pb-3">
            <div className="flex items-start justify-between gap-4">
              <DrawerTitle className="font-field-64 text-[22px] leading-7 text-slate-50">
                {title}
              </DrawerTitle>
              <DrawerClose asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="-mr-2 -mt-1 h-9 w-9 text-slate-400 hover:bg-slate-600 hover:text-slate-50"
                  aria-label={ManageGroupsContent.actions.close}
                  disabled={isSubmitting}
                >
                  <XIcon size={18} weight="bold" />
                </Button>
              </DrawerClose>
            </div>
          </DrawerHeader>

          <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-4">
            <p className="mb-3 text-body text-slate-400">{description}</p>
            <ul className="space-y-2">
              {bullets.map((bullet, index) => (
                <li
                  key={index}
                  className="flex items-start gap-2 text-body text-slate-300"
                >
                  <CheckCircleIcon
                    size={16}
                    weight="duotone"
                    className="mt-0.5 shrink-0 text-slate-500"
                  />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </div>

          <DrawerFooter className="flex-row gap-2 px-2 pt-2">
            <DrawerClose asChild>
              <Button
                variant="outline"
                size="lg"
                disabled={isSubmitting}
                className="h-12 flex-1 border-slate-550 bg-transparent text-slate-50 hover:bg-slate-600"
              >
                {ManageGroupsContent.actions.cancel}
              </Button>
            </DrawerClose>
            <Button
              size="lg"
              onClick={handleConfirm}
              disabled={isSubmitting}
              className={
                isArchive
                  ? 'h-12 flex-1 bg-red-500 text-slate-50 hover:bg-red-600'
                  : 'h-12 flex-1 bg-purple-300 text-slate-700 hover:bg-purple-400'
              }
            >
              {isSubmitting ? loadingLabel : confirmLabel}
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
